import React, { useState, useEffect } from "react";
import { SharePointFile } from "../../interfaces/sharePointTypes";
import { useSharePoint } from "../../hooks/useSharePoint";
import WordIcon from "../../../assets/Word.svg";
import PDFIcon from "../../../assets/PDF.svg";
import { createLogger } from "../../utils/logger";
import ErrorMessage from "./ErrorMessage";

const log = createLogger("DocumentLibraryModal");

interface DocumentLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectDocuments: (documents: SharePointFile[]) => void;
  currentlyAttachedFileIds?: Set<string>; // NEW: Currently attached SharePoint file IDs
}

export const DocumentLibraryModal: React.FC<DocumentLibraryModalProps> = ({
  isOpen,
  onClose,
  onSelectDocuments,
  currentlyAttachedFileIds = new Set(), // NEW: with default value
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDocuments, setSelectedDocuments] = useState<SharePointFile[]>([]);
  const [mode, setMode] = useState<"select" | "manage">("select");
  const [libraryDocuments, setLibraryDocuments] = useState<SharePointFile[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  // SharePoint integration
  const { listFiles, uploadFile, isInitialized } = useSharePoint();

  // Load files from SharePoint when modal opens
  useEffect(() => {
    if (isOpen && isInitialized) {
      loadFiles();
    }
  }, [isOpen, isInitialized]);

  // Sync selection state with currently attached files when library documents load
  useEffect(() => {
    if (isOpen && libraryDocuments.length > 0) {
      // Initialize selection to match currently attached files
      const initialSelection = libraryDocuments.filter((doc) =>
        currentlyAttachedFileIds.has(doc.id)
      );
      setSelectedDocuments(initialSelection);

      log.info("Synced modal selection with carousel", {
        attachedCount: currentlyAttachedFileIds.size,
        selectedCount: initialSelection.length,
      });
    }
  }, [isOpen, libraryDocuments, currentlyAttachedFileIds]);

  const loadFiles = async () => {
    setIsLoadingFiles(true);
    setLoadingMessage("Loading documents...");
    setError(null); // Clear previous errors
    try {
      const files = await listFiles(searchQuery || undefined);
      setLibraryDocuments(files);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to load files from SharePoint";
      setError(errorMessage);
      log.error("Failed to load files from SharePoint", { error });
    } finally {
      setIsLoadingFiles(false);
      setLoadingMessage("");
    }
  };

  if (!isOpen) return null;

  // Client-side filtering by name only
  const filteredDocuments = searchQuery
    ? libraryDocuments.filter((doc) => doc.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : libraryDocuments;

  const toggleDocument = (doc: SharePointFile) => {
    const isSelected = selectedDocuments.some((d) => d.id === doc.id);
    if (isSelected) {
      setSelectedDocuments(selectedDocuments.filter((d) => d.id !== doc.id));
    } else {
      setSelectedDocuments([...selectedDocuments, doc]);
    }
  };

  const handleConfirm = () => {
    log.info("Documents selected from library", {
      count: selectedDocuments.length,
      documents: selectedDocuments.map((d) => ({ id: d.id, name: d.name })),
    });

    // Close modal immediately for instant feedback
    // Files will download in background and appear in carousel as ready
    onClose();

    // Trigger file downloads (async, non-blocking)
    onSelectDocuments(selectedDocuments);
  };

  const formatFileSize = (bytes: number): string => {
    const kb = bytes / 1024;
    return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.round(kb)} KB`;
  };

  const formatDate = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const getFileIcon = (file: SharePointFile) => {
    // Check MIME type first
    if (file.mimeType === "application/pdf") {
      return PDFIcon;
    }

    // Fallback to file extension
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (extension === "pdf") {
      return PDFIcon;
    }

    // Default to Word icon for .docx and other files
    return WordIcon;
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setIsLoadingFiles(true);
      setLoadingMessage(`Uploading ${files.length} file${files.length > 1 ? "s" : ""}...`);
      setError(null); // Clear previous errors
      try {
        // Upload each file to SharePoint
        const uploadPromises = Array.from(files).map((file) => uploadFile(file));
        const uploadedFiles = await Promise.all(uploadPromises);

        log.info("Documents uploaded to SharePoint", {
          count: uploadedFiles.length,
          documents: uploadedFiles.map((d) => ({ id: d.id, name: d.name })),
        });

        // Refresh the file list
        await loadFiles();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Failed to upload files";
        setError(errorMessage);
        log.error("Failed to upload files", { error });
      } finally {
        setIsLoadingFiles(false);
        setLoadingMessage("");
      }
    }
    // Reset input
    event.target.value = "";
  };

  const handleViewDocument = (webUrl: string | null) => {
    if (webUrl) {
      log.info("Opening SharePoint document in browser", { webUrl });
      window.open(webUrl, "_blank", "noopener,noreferrer");
    } else {
      log.warn("Document webUrl is null, cannot open");
      setError("Document URL is not available");
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
      }}
    >
      <div
        style={{
          position: "relative",
          background: "white",
          borderRadius: "8px",
          width: "90%",
          maxWidth: "600px",
          maxHeight: "70vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: "18px",
                fontWeight: 600,
                color: "#111827",
              }}
            >
              Document Library
            </h3>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                fontSize: "24px",
                cursor: "pointer",
                color: "#6b7280",
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "6px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#f3f4f6";
                e.currentTarget.style.color = "#111827";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "none";
                e.currentTarget.style.color = "#6b7280";
              }}
            >
              ×
            </button>
          </div>

          {/* Tab Navigation */}
          <div
            style={{
              display: "flex",
              gap: "24px",
              borderBottom: "2px solid #f3f4f6",
              marginLeft: "-24px",
              marginRight: "-24px",
              paddingLeft: "24px",
            }}
          >
            <button
              onClick={() => setMode("select")}
              style={{
                padding: "8px 0",
                background: "none",
                color: mode === "select" ? "#0066cc" : "#6b7280",
                border: "none",
                borderBottom: mode === "select" ? "2px solid #0066cc" : "2px solid transparent",
                fontSize: "14px",
                fontWeight: mode === "select" ? 600 : 400,
                cursor: "pointer",
                marginBottom: "-2px",
                transition: "all 0.2s",
              }}
            >
              Select Documents
            </button>
            <button
              onClick={() => setMode("manage")}
              style={{
                padding: "8px 0",
                background: "none",
                color: mode === "manage" ? "#0066cc" : "#6b7280",
                border: "none",
                borderBottom: mode === "manage" ? "2px solid #0066cc" : "2px solid transparent",
                fontSize: "14px",
                fontWeight: mode === "manage" ? 600 : 400,
                cursor: "pointer",
                marginBottom: "-2px",
                transition: "all 0.2s",
              }}
            >
              Manage Library
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{ padding: "16px 24px 0 24px" }}>
            <ErrorMessage error={error} onDismiss={() => setError(null)} />
          </div>
        )}

        {/* Search */}
        <div style={{ padding: "16px 24px", borderBottom: "1px solid #e5e7eb" }}>
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            {/* Search Icon */}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              style={{
                position: "absolute",
                left: "12px",
                color: "#9ca3af",
              }}
            >
              <path
                d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 40px",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "14px",
                outline: "none",
                transition: "all 0.2s",
                backgroundColor: "#f9fafb",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "#0066cc";
                e.currentTarget.style.backgroundColor = "white";
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.backgroundColor = "#f9fafb";
              }}
            />
            {/* Clear button */}
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                style={{
                  position: "absolute",
                  right: "12px",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#9ca3af",
                  transition: "color 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#6b7280";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "#9ca3af";
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Document List */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "16px",
            backgroundColor: "#fafbfc",
          }}
        >
          {mode === "manage" && (
            /* Upload Area for Manage Mode */
            <div
              style={{
                padding: "24px",
                margin: "0 0 16px 0",
                border: "2px dashed #d1d5db",
                borderRadius: "12px",
                textAlign: "center",
                background: "linear-gradient(to bottom, #ffffff, #f9fafb)",
                transition: "all 0.3s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#0066cc";
                e.currentTarget.style.background = "linear-gradient(to bottom, #f0f7ff, #ffffff)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#d1d5db";
                e.currentTarget.style.background = "linear-gradient(to bottom, #ffffff, #f9fafb)";
              }}
            >
              <input
                type="file"
                id="library-upload"
                multiple
                accept=".docx,.pdf,.txt"
                onChange={handleFileUpload}
                style={{ display: "none" }}
              />
              <div style={{ marginBottom: "12px" }}>
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{ margin: "0 auto", color: "#9ca3af" }}
                >
                  <path
                    d="M7 16V12M7 12V8M7 12H17M17 12V8M17 12V16M12 7V17M12 7L8 11M12 7L16 11"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                </svg>
              </div>
              <label
                htmlFor="library-upload"
                style={{
                  display: "inline-block",
                  padding: "10px 20px",
                  background: "#0066cc",
                  color: "white",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 500,
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#0052a3";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#0066cc";
                }}
              >
                Upload Documents
              </label>
              <p style={{ margin: "12px 0 0 0", fontSize: "13px", color: "#6b7280" }}></p>
              <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "#9ca3af" }}>
                Supported formats: DOCX, PDF, TXT
              </p>
            </div>
          )}

          {filteredDocuments.map((doc) => {
            const isSelected = selectedDocuments.some((d) => d.id === doc.id);
            return (
              <div
                key={doc.id}
                onClick={mode === "select" ? () => toggleDocument(doc) : undefined}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px 16px",
                  margin: "0 0 8px 0",
                  borderRadius: "8px",
                  cursor: mode === "select" ? "pointer" : "default",
                  background: mode === "select" && isSelected ? "#f0f7ff" : "white",
                  border: `1px solid ${mode === "select" && isSelected ? "#0066cc" : "#e5e7eb"}`,
                  transition: "all 0.2s",
                  boxShadow:
                    mode === "select" && isSelected
                      ? "0 1px 3px rgba(0, 102, 204, 0.1)"
                      : "0 1px 2px rgba(0, 0, 0, 0.05)",
                }}
                onMouseEnter={(e) => {
                  if (mode === "select") {
                    e.currentTarget.style.boxShadow = isSelected
                      ? "0 2px 6px rgba(0, 102, 204, 0.15)"
                      : "0 2px 4px rgba(0, 0, 0, 0.1)";
                    if (!isSelected) {
                      e.currentTarget.style.background = "#f9fafb";
                    }
                  }
                }}
                onMouseLeave={(e) => {
                  if (mode === "select") {
                    e.currentTarget.style.boxShadow = isSelected
                      ? "0 1px 3px rgba(0, 102, 204, 0.1)"
                      : "0 1px 2px rgba(0, 0, 0, 0.05)";
                    if (!isSelected) {
                      e.currentTarget.style.background = "white";
                    }
                  }
                }}
              >
                {mode === "select" ? (
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "4px",
                      border: `2px solid ${isSelected ? "#0066cc" : "#d1d5db"}`,
                      backgroundColor: isSelected ? "#0066cc" : "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s",
                      flexShrink: 0,
                    }}
                  >
                    {isSelected && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M20 6L9 17L4 12"
                          stroke="white"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                ) : (
                  <div style={{ width: "20px" }} />
                )}
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "8px",
                    backgroundColor: "#f3f4f6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {(() => {
                    const Icon = getFileIcon(doc);
                    return <Icon style={{ maxWidth: "26px", maxHeight: "26px", height: "auto" }} />;
                  })()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 500,
                      fontSize: "14px",
                      color: "#111827",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {doc.name}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      marginTop: "4px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span>{formatDate(doc.modifiedAt ?? doc.createdAt)}</span>
                    <span style={{ color: "#d1d5db" }}>•</span>
                    <span>{formatFileSize(doc.size ?? 0)}</span>
                  </div>
                </div>
                {mode === "manage" && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDocument(doc.webUrl);
                    }}
                    disabled={!doc.webUrl}
                    style={{
                      padding: "6px 12px",
                      background: "white",
                      color: doc.webUrl ? "#0066cc" : "#9ca3af",
                      border: doc.webUrl ? "1px solid #bfdbfe" : "1px solid #e5e7eb",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: 500,
                      cursor: doc.webUrl ? "pointer" : "not-allowed",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      if (doc.webUrl) {
                        e.currentTarget.style.background = "#eff6ff";
                        e.currentTarget.style.borderColor = "#93c5fd";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (doc.webUrl) {
                        e.currentTarget.style.background = "white";
                        e.currentTarget.style.borderColor = "#bfdbfe";
                      }
                    }}
                  >
                    View
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 24px",
            borderTop: "1px solid #e5e7eb",
            backgroundColor: "white",
          }}
        >
          <span style={{ fontSize: "13px", color: "#6b7280", fontWeight: 500 }}>
            {mode === "select"
              ? `${selectedDocuments.length} document${selectedDocuments.length !== 1 ? "s" : ""} selected`
              : `${libraryDocuments.length} document${libraryDocuments.length !== 1 ? "s" : ""} in library`}
          </span>
          <div style={{ display: "flex", gap: "8px" }}>
            {mode === "select" ? (
              <>
                <button
                  onClick={onClose}
                  style={{
                    padding: "8px 20px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    background: "white",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#374151",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#f9fafb";
                    e.currentTarget.style.borderColor = "#d1d5db";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "white";
                    e.currentTarget.style.borderColor = "#e5e7eb";
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={selectedDocuments.length === 0}
                  style={{
                    padding: "8px 20px",
                    border: "none",
                    borderRadius: "6px",
                    background: selectedDocuments.length > 0 ? "#0066cc" : "#e5e7eb",
                    color: selectedDocuments.length > 0 ? "white" : "#9ca3af",
                    cursor: selectedDocuments.length > 0 ? "pointer" : "not-allowed",
                    fontSize: "14px",
                    fontWeight: 500,
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedDocuments.length > 0) {
                      e.currentTarget.style.background = "#0052a3";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedDocuments.length > 0) {
                      e.currentTarget.style.background = "#0066cc";
                    }
                  }}
                >
                  Add to Chat
                </button>
              </>
            ) : (
              <button
                onClick={onClose}
                style={{
                  padding: "8px 20px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                  background: "white",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#374151",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#f9fafb";
                  e.currentTarget.style.borderColor = "#d1d5db";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "white";
                  e.currentTarget.style.borderColor = "#e5e7eb";
                }}
              >
                Close
              </button>
            )}
          </div>
        </div>

        {/* Loading Overlay */}
        {isLoadingFiles && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              borderRadius: "8px",
            }}
          >
            <div
              style={{
                width: "32px",
                height: "32px",
                border: "3px solid #E0E0E0",
                borderTop: "3px solid #0078D4",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
            <p
              style={{
                marginTop: "12px",
                color: "#757575",
                fontSize: "14px",
                fontFamily: "'Segoe UI', sans-serif",
                margin: "12px 0 0 0",
              }}
            >
              {loadingMessage || "Loading..."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
